import { type DriveStep, driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import './tour.css';

/**
 * First-run guided tour (coachmarks). Spotlights the core workflow on the
 * dashboard one step at a time. Auto-launches once after onboarding (see
 * DashboardRoute) and can be replayed from the "Take a tour" button.
 *
 * Steps anchor on elements that are always present on the dashboard
 * (data-tour="…") so the popovers never point at nothing. Steps whose target
 * is missing are skipped rather than erroring.
 */

const TOUR_DONE_KEY = 'develio:tour:done';

export function hasSeenTour(): boolean {
  try {
    return window.localStorage.getItem(TOUR_DONE_KEY) === '1';
  } catch {
    return false;
  }
}

function markTourSeen() {
  try {
    window.localStorage.setItem(TOUR_DONE_KEY, '1');
  } catch {
    // ignore (private mode, etc.) — worst case the tour shows again
  }
}

const ALL_STEPS: DriveStep[] = [
  {
    popover: {
      title: 'Welcome to Develio 👋',
      description:
        'A quick 60-second tour of how to turn a development project into a defensible fiscal impact report. You can skip anytime.',
    },
  },
  {
    element: '[data-tour="new-analysis"]',
    popover: {
      title: 'Run an impact analysis',
      description:
        'This is the heart of Develio. Walk through a guided wizard and get a branded report with jobs, tax revenue, and ROI for any project.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-tour="jurisdiction"]',
    popover: {
      title: 'Set up your jurisdiction',
      description:
        'Enter your tax rates, district, and assumptions once. Every analysis then reflects your community automatically.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-tour="market-intelligence"]',
    popover: {
      title: 'Explore your market intelligence',
      description:
        'Community Health, Workforce, Industry Targets, and Wage Benchmarks for your area — pulled from federal data, ready to cite.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '[data-tour="billing"]',
    popover: {
      title: 'Your trial',
      description:
        'You have 2 full analyses on the trial. Start your subscription anytime here to unlock unlimited — otherwise it begins automatically when the trial ends.',
      side: 'right',
      align: 'start',
    },
  },
  {
    popover: {
      title: "You're all set",
      description:
        'Start with your first analysis whenever you are ready. You can replay this tour anytime from "Take a tour" on the dashboard.',
    },
  },
];

/**
 * Launch the tour. Drops any steps whose anchor element is not currently in the
 * DOM so a collapsed section or missing card never breaks the flow.
 */
export function startTour() {
  const steps = ALL_STEPS.filter(
    (s) => !s.element || document.querySelector(s.element as string) != null,
  );

  const d = driver({
    showProgress: true,
    allowClose: true,
    nextBtnText: 'Next',
    prevBtnText: 'Back',
    doneBtnText: 'Done',
    popoverClass: 'develio-tour',
    steps,
    onDestroyed: () => markTourSeen(),
  });

  d.drive();
}
